import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LinkMode, LinkPolicyDTO } from '@sac/contracts'
import { api, ApiError } from '../../lib/api'
import { useToast } from '../ui/Toaster'

/**
 * Configurações → Links. Quais domínios podem sair em cada canal, para tudo que sai: texto
 * digitado, sugestão da IA, resposta automática e o agente do MCP. Só admin — a API devolve 403
 * para o resto.
 */

const when = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

function Policy({ p }: { p: LinkPolicyDTO }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [mode, setMode] = useState<LinkMode>(p.mode)
  const [text, setText] = useState(p.domains.join('\n'))
  const domains = text.split(/[\s,;]+/).map((d) => d.trim()).filter(Boolean)
  const dirty = mode !== p.mode || domains.join('\n') !== p.domains.join('\n')

  const save = useMutation({
    mutationFn: () => api.saveLinkPolicy(p.scope, { mode, domains }),
    onSuccess: () => {
      toast(`Regra de ${p.label} salva.`)
      void qc.invalidateQueries({ queryKey: ['link-policies'] })
    },
    onError: (e: Error) => toast(e instanceof ApiError && e.detail ? e.detail : 'Domínio inválido na lista. Use só o domínio, como youtube.com.'),
  })

  return (
    <div className="cfg-block">
      <div className="blk-h">
        {p.label}
        {p.isDefault && <span className="cnt">padrão</span>}
      </div>
      <select value={mode} onChange={(e) => setMode(e.target.value as LinkMode)} style={{ marginBottom: 8 }}>
        <option value="allow">Só permitir os domínios abaixo</option>
        <option value="block">Permitir tudo, menos os domínios abaixo</option>
      </select>
      <textarea
        rows={Math.max(2, Math.min(8, domains.length + 1))}
        value={text}
        placeholder={mode === 'allow' ? 'Nenhum domínio: nenhum link sai neste canal.' : 'Nenhum domínio: qualquer link sai.'}
        onChange={(e) => setText(e.target.value)}
        style={{ width: '100%', font: 'inherit', fontSize: 13, fontFamily: 'ui-monospace, Menlo, monospace' }}
      />
      <div className="cfg-note" style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
        <span style={{ flex: 1 }}>Um domínio por linha. Vale também para os subdomínios (smartenvios.com cobre portal.smartenvios.com).</span>
        <button className="btn pri sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          Salvar
        </button>
      </div>
    </div>
  )
}

export function LinksTab() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data: policies, isLoading } = useQuery({ queryKey: ['link-policies'], queryFn: api.linkPolicies })
  const { data: requests = [] } = useQuery({ queryKey: ['link-requests'], queryFn: api.linkRequests, refetchInterval: 60_000 })

  const resolve = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'allow' | 'deny' }) => api.resolveLinkRequest(id, action),
    onSuccess: (_d, v) => {
      toast(v.action === 'allow' ? 'Domínio liberado.' : 'Pedido recusado.')
      void qc.invalidateQueries({ queryKey: ['link-requests'] })
      void qc.invalidateQueries({ queryKey: ['link-policies'] })
    },
  })

  return (
    <>
      <div className="crm-head">
        <h2>Links</h2>
        <p>
          Quais domínios podem ser enviados em cada canal. Vale para tudo que sai do SAC: o que a equipe digita, as
          sugestões da IA, a resposta automática e o agente externo. Quem esbarrar num link bloqueado pode pedir a
          liberação, e o pedido aparece aqui.
        </p>
      </div>

      {requests.length > 0 && (
        <div className="cfg-block">
          <div className="blk-h">
            Pedidos de liberação <span className="cnt">{requests.length}</span>
          </div>
          {requests.map((r) => (
            <div className="cfg-row" key={r.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{r.domain}</b> em {r.scopeLabel}
                <div className="cfg-note">
                  {r.requestedBy ?? 'Alguém'} · {when(r.createdAt)}
                  {r.sample && <> · “{r.sample.length > 160 ? r.sample.slice(0, 160) + '…' : r.sample}”</>}
                </div>
              </div>
              <button className="btn ghost sm" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: r.id, action: 'deny' })}>
                Recusar
              </button>
              <button className="btn pri sm" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: r.id, action: 'allow' })}>
                Liberar
              </button>
            </div>
          ))}
        </div>
      )}

      {isLoading ? <div className="empty">Carregando…</div> : policies?.map((p) => <Policy key={`${p.scope}:${p.mode}:${p.domains.join(',')}`} p={p} />)}
    </>
  )
}
